import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { DEFAULT_IMAGE, SITE_STRUCTURED_DATA, getPageMetadata, serializeStructuredData } from '@/lib/seo/metadata.mjs';

interface SEOComponentProps {
  metadataOnly?: boolean;
  title?: string;
  description?: string;
  canonicalUrl?: string;
  keywords?: string;
  image?: string;
  ogType?: 'website' | 'article' | 'product' | 'video';
  noIndex?: boolean;
  structuredData?: object | object[];
  alternateUrls?: { hreflang: string; href: string }[];
  breadcrumbs?: { name: string; url: string }[];
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  articleSection?: string;
  videoUrl?: string;
  videoDuration?: string;
  priceRange?: string;
  availability?: 'InStock' | 'OutOfStock' | 'PreOrder';
}

function SEOComponent({ 
  metadataOnly = false,
  title,
  description,
  canonicalUrl,
  keywords = 'escrow platform India, online business deals, secure transactions, trust infrastructure, WhatsApp deal escrow, freelance escrow India, B2B deal protection',
  image = DEFAULT_IMAGE,
  ogType = 'website',
  noIndex = false,
  structuredData,
  alternateUrls = [],
  breadcrumbs,
  author = 'Busiconn',
  publishedTime,
  modifiedTime,
  articleSection,
  videoUrl,
  videoDuration,
  priceRange,
  availability
}: SEOComponentProps) {
  const { pathname } = useLocation();
  const page = getPageMetadata(pathname);
  title ??= page.title;
  description ??= page.description;
  canonicalUrl ??= page.canonicalUrl;
  noIndex = noIndex || page.noIndex;
  structuredData ??= pathname === '/' ? SITE_STRUCTURED_DATA : undefined;
  const robots = noIndex ? 'noindex, follow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';

  // Generate breadcrumb structured data
  const breadcrumbStructuredData = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url
    }))
  } : null;

  // Combine all structured data
  const allStructuredData = [
    structuredData,
    breadcrumbStructuredData
  ].filter(Boolean);

  // Generate comprehensive meta tags based on content type
  const generateMetaTags = () => {
    const baseTags: (JSX.Element)[] = [
      // Basic SEO
      <meta key="description" name="description" content={description} />,
      <meta key="keywords" name="keywords" content={keywords} />,
      <meta key="author" name="author" content={author} />,
      <meta key="robots" name="robots" content={robots} />,
      <meta key="googlebot" name="googlebot" content={robots} />,
      <meta key="bingbot" name="bingbot" content={robots} />,
      
      // Advanced SEO
      <meta key="language" name="language" content="en-IN" />,
      <meta key="geo-region" name="geo.region" content="IN" />,
      <meta key="geo-placename" name="geo.placename" content="India" />,
      <meta key="distribution" name="distribution" content="global" />,
      <meta key="rating" name="rating" content="general" />,
      <meta key="revisit-after" name="revisit-after" content="1 day" />,
      
      // AI and Voice Search Optimization
      
      // Social Commerce Specific
// Business & Infrastructure Classification
      <meta key="application-type" name="application-type" content="Trust and Escrow Infrastructure" />,
      <meta key="business-type" name="business-type" content="Financial Infrastructure, Deal Safety Platform" />,
      <meta key="use-case" name="use-case" content="Secure Online Business Transactions, Escrow Payments, Deal Protection" />,

      // Open Graph Tags
      <meta key="og:site_name" property="og:site_name" content="BUSICONN" />,
      <meta key="og:title" property="og:title" content={title} />,
      <meta key="og:description" property="og:description" content={description} />,
      <meta key="og:type" property="og:type" content={ogType} />,
      <meta key="og:url" property="og:url" content={canonicalUrl} />,
      <meta key="og:image" property="og:image" content={image} />,
      <meta
        key="og:image:alt"
        property="og:image:alt"
        content={`${title} - Busiconn Trust & Escrow Infrastructure`}
      />,
      <meta key="og:locale" property="og:locale" content="en_IN" />,
      
      // Twitter Cards
      <meta key="twitter:card" name="twitter:card" content="summary_large_image" />,
      <meta key="twitter:site" name="twitter:site" content="@busiconn1997" />,
      <meta key="twitter:creator" name="twitter:creator" content="@busiconn1997" />,
      <meta key="twitter:title" name="twitter:title" content={title} />,
      <meta key="twitter:description" name="twitter:description" content={description} />,
      <meta key="twitter:url" name="twitter:url" content={canonicalUrl} />,
      <meta key="twitter:image" name="twitter:image" content={image} />,
      <meta key="twitter:image:alt" name="twitter:image:alt" content={`${title} - BUSICONN`} />,
      
      // LinkedIn
      <meta key="linkedin:owner" property="linkedin:owner" content="https://www.linkedin.com/in/busiconn-com-267427340/" />,
      <meta key="linkedin:title" name="linkedin:title" content={title} />,
      <meta key="linkedin:description" name="linkedin:description" content={description} />,
      <meta key="linkedin:url" name="linkedin:url" content={canonicalUrl} />,
      <meta key="linkedin:image" name="linkedin:image" content={image} />,
      
      // Canonical and Alternate URLs
      <link key="canonical" rel="canonical" href={canonicalUrl} />,
      ...alternateUrls.map((alt, index) => (
        <link key={`alternate-${index}`} rel="alternate" hrefLang={alt.hreflang} href={alt.href} />
      ))
    ];

    // Add article-specific tags
    if (ogType === 'article') {
      baseTags.push(<meta key="article:author" property="article:author" content={author} />);
      
      if (publishedTime) {
        baseTags.push(<meta key="article:published_time" property="article:published_time" content={publishedTime} />);
      }
      
      if (modifiedTime) {
        baseTags.push(<meta key="article:modified_time" property="article:modified_time" content={modifiedTime} />);
      }
      
      if (articleSection) {
        baseTags.push(<meta key="article:section" property="article:section" content={articleSection} />);
      }
    }

    // Add video-specific tags
    if (ogType === 'video' && videoUrl) {
      baseTags.push(<meta key="og:video" property="og:video" content={videoUrl} />);
      baseTags.push(<meta key="og:video:type" property="og:video:type" content="video/mp4" />);
      
      if (videoDuration) {
        baseTags.push(<meta key="video:duration" property="video:duration" content={videoDuration} />);
      }
    }

    // Add product-specific tags
    if (ogType === 'product') {
      if (priceRange) {
        baseTags.push(<meta key="product:price:range" property="product:price:range" content={priceRange} />);
      }
      
      if (availability) {
        baseTags.push(<meta key="product:availability" property="product:availability" content={availability} />);
      }
      
      baseTags.push(<meta key="product:brand" property="product:brand" content="BUSICONN" />);
    }

    return baseTags;
  };

  return (
    <Helmet>
      <title>{title}</title>
      
      {/* Meta Tags */}
      {generateMetaTags()}
      
      {/* Performance Optimization */}
      <link key="preconnect-fonts" rel="preconnect" href="https://fonts.googleapis.com" />
      <link key="preconnect-fonts-static" rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link key="preconnect-cashfree" rel="preconnect" href="https://sdk.cashfree.com" />
      <link key="dns-prefetch-gtm" rel="dns-prefetch" href="https://www.googletagmanager.com" />
      
      {/* Structured Data */}
      {!noIndex && allStructuredData.length > 0 && allStructuredData.map((data, index) => (
        data && (
          <script 
            key={`structured-data-${index}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: serializeStructuredData(Array.isArray(data) ? data : [data])
            }}
          />
        )
      ))}
      
      {/* Additional Performance Headers */}
      <meta key="http-equiv-dns" httpEquiv="x-dns-prefetch-control" content="on" />
      
      {/* Security Headers */}
      <meta key="referrer" name="referrer" content="strict-origin-when-cross-origin" />
      
      {/* Mobile Optimization */}
      {!metadataOnly && <meta key="format-detection" name="format-detection" content="telephone=no" />}
      {!metadataOnly && <meta key="mobile-web-app" name="mobile-web-app-capable" content="yes" />}
      {!metadataOnly && <meta key="apple-mobile-app" name="apple-mobile-web-app-capable" content="yes" />}
      {!metadataOnly && <meta key="apple-mobile-status" name="apple-mobile-web-app-status-bar-style" content="black-translucent" />}
      
      {/* Favicon and App Icons */}
      <link key="icon" rel="icon" type="image/svg+xml" href="/assets/images/favicon.svg" />
      <link key="apple-touch-icon" rel="apple-touch-icon" href="/assets/images/profile.png" />
      <link key="manifest" rel="manifest" href="/manifest.json" />
      {!metadataOnly && <meta key="theme-color" name="theme-color" content="#ffffff" />}
    </Helmet>
  );
}

export default SEOComponent;

// Export types for use in other components
export type { SEOComponentProps };
